import type { AutoRechargeConfig, AutoRechargeForm, DocumentInfo, PaymentMethod, PaymentTransactionsResponse, TransactionsResponse } from "@/schemas/billing.schema";
import { api } from "@/services/api";

export const billingApi = {
  // ============= Checkout =============

  async chargeQuote(amount: number) {
    const { data } = await api.get<{ credits: number; fee_bps: number; fee: number; total: number; currency: string }>("/billing/charge-quote", { params: { amount } });
    return data;
  },

  async charge(amount: number, expectedTotal: number) {
    const { data } = await api.post<{ url: string }>("/billing/charge", {
      amount,
      expected_total: expectedTotal,
    });

    return data;
  },

  async chargeWithMethod(amount: number, paymentMethodId: string, expectedTotal: number) {
    const { data } = await api.post<{ success: boolean; transaction_id: string }>(
      "/billing/charge-method",
      { amount, payment_method_id: paymentMethodId, expected_total: expectedTotal },
    );
    return data;
  },

  async invoice(invoiceId: string) {
    const { data } = await api.get<DocumentInfo>(`/billing/invoice/${invoiceId}`);

    return data;
  },

  // ============= Payment Methods =============

  async getPaymentMethods() {
    const { data } = await api.get<PaymentMethod[]>("/billing/payment-methods");
    return data;
  },

  async setupPaymentMethod(body?: { success_url?: string; cancel_url?: string }) {
    const { data } = await api.post<{ session_id: string; url: string }>(
      "/billing/payment-methods/setup",
      body,
    );
    return data;
  },

  async setDefaultPaymentMethod(id: string) {
    await api.patch(`/billing/payment-methods/${id}/default`);
  },

  async deletePaymentMethod(id: string, force?: boolean) {
    await api.delete(`/billing/payment-methods/${id}`, { params: force ? { force: true } : undefined });
  },

  async reactivatePaymentMethod(id: string) {
    await api.patch(`/billing/payment-methods/${id}/reactivate`);
  },

  // ============= Auto-Recharge =============

  async getAutoRecharge() {
    const { data } = await api.get<AutoRechargeConfig | null>("/billing/auto-recharge");
    return data;
  },

  async updateAutoRecharge(config: AutoRechargeForm) {
    const { data } = await api.put<AutoRechargeConfig>("/billing/auto-recharge", config);
    return data;
  },

  // ============= Transactions =============

  async getTransactions(params: {
    type?: string;
    source?: string;
    page?: number;
    limit?: number;
  } = {}) {
    const { data } = await api.get<TransactionsResponse>("/billing/transactions", { params });
    return data;
  },

  // ============= Payment Transactions (actual charges) =============

  async getPaymentTransactions(params: {
    type?: string;
    page?: number;
    limit?: number;
  } = {}) {
    const { data } = await api.get<PaymentTransactionsResponse>("/billing/payment-transactions", { params });
    return data;
  },
};
